import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/compare/uptimerobot')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'UptimeRobot Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs UptimeRobot: plans, check intervals, status pages, and how each confirms downtime. Uptrack checks every 30s from $3/mo." },
      { property: 'og:title', content: 'UptimeRobot Alternative — Uptrack' },
      { property: 'og:description', content: "Compare Uptrack and UptimeRobot on price, check interval, status pages, and how each confirms downtime before it alerts." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-uptimerobot.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/uptimerobot' },
    ],
    scripts: comparisonScripts('uptimerobot', "UptimeRobot"),
  }),
})

const data: ComparisonData = {
  competitor: "UptimeRobot",
  tagline: "UptimeRobot has a free plan with 50 monitors at 5-minute checks, and 30-second checks from its Team plan. Uptrack checks every monitor every 30 seconds, from $3/mo.",
  heroDescription: "These facts come from UptimeRobot's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Free plan", uptrack: "5 monitors at 30 s from Nuremberg", competitor: "50 monitors at 5 min (commercial use allowed)" },
    { feature: "Cheapest paid plan", uptrack: "Pro $3/mo: 50 monitors at 30s", competitor: "Solo $10/mo: up to 50 monitors at 60s", uptrackWins: true },
    { feature: "30-second checks", uptrack: "Every plan, from $3/mo", competitor: "Team plan, $41/mo", uptrackWins: true },
    { feature: "Fastest check interval", uptrack: "30 sec", competitor: "15 sec (Scale plan)" },
    { feature: "Status pages (cheapest paid plan)", uptrack: "5 on Pro ($3/mo)", competitor: "3 on Solo ($10/mo)", uptrackWins: true },
    { feature: "Check regions", uptrack: MONITOR_LOCATIONS.compare.cell, competitor: "4 selectable regions on paid plans", uptrackWins: MONITOR_LOCATIONS.compare.wins },
  ],

  painPoints: [
    {
      title: "30-second checks start on Team",
      description: "UptimeRobot's free plan checks every 5 minutes and Solo checks every 60 seconds. 30-second checks start on the Team plan at $41/mo. Every Uptrack plan checks every 30 seconds, from $3/mo.",
    },
    {
      title: "Different down confirmation",
      description: "For a timeout, UptimeRobot retries and then checks from 2 more locations before it marks a monitor down. An HTTP error status marks the monitor down at once. Uptrack waits for 2-5 consecutive failed checks, and several check locations must confirm.",
    },
    {
      title: "Status pages per plan",
      description: "UptimeRobot's free plan includes 1 basic status page and Solo includes 3. Uptrack Pro includes 5 status pages, and Team and Business include unlimited pages.",
    },
  ],

  differentiators: [
    "Alert confirmation: Uptrack opens an incident only after 2-5 consecutive failed checks, and several check locations must confirm. One DNS timeout or CDN blip does not page you.",
    "30-second checks on every plan: Pro is $3/mo for 50 monitors, Team is $20/mo for 200, Business is $79/mo for 1,000.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "Status pages on every plan: 1 on free, 5 on Pro, unlimited on Team and Business, with email subscriber notifications on paid plans.",
    "Flat, published pricing: $3, $20, or $79 per month. No per-seat or usage charges, and a 30-day money-back guarantee.",
  ],

  notForYou: [
    "If you need more than 5 free monitors, UptimeRobot offers 50 monitors at 5-minute checks, and its terms now allow commercial use. Uptrack's free plan is 5 monitors at 30 seconds from one location, and it also allows commercial use.",
    "If you need checks faster than 30 seconds, UptimeRobot's Scale plan checks every 15 seconds.",
    "If you need a native mobile app, UptimeRobot has iOS and Android apps. Uptrack is web-first.",
    "If you want bundled APM, logs, or server metrics, Uptrack focuses on uptime, browser checks, cron/heartbeat, and status pages, not full observability.",
  ],
}
